<template>
  <SupportBanner
    v-if="supportBanner"
    v-bind="supportBanner"
    :cms-page-id="homeData?.id"
  />

  <SkinnyBanner data-test="home-skinny-banner" page="home" />
  <!-- Short term solution to have a H1 for SEO and A11y purpose on the home page -->
  <!-- Todo: design a homepage element to include the H1 -->
  <!-- see [LIF-1601](https://backmarket.atlassian.net/browse/LIF-1601) -->
  <h1 id="home-main-title" class="sr-only">{{ homeData?.title }}</h1>

  <meta :content="COMPANY_NAME" itemprop="name" />
  <meta :content="homeData?.meta.image.src" itemprop="logo" />

  <HeroView
    v-if="banner"
    :align="homeData?.align"
    :badge="homeData?.badge"
    :banner
    class="mb-44"
    headingType="p"
    :subtitle="homeData?.subtitle"
    :title="homeData?.title"
  />

  <!-- Render first Carousel block before TheMainContent if it exists -->
  <Container
    v-if="firstBlock && firstBlockIsCarousel"
    :width="getBlockWidth(firstBlock.type)"
  >
    <BlockRenderer
      :id="firstBlock.id"
      :block-position="firstBlock.blockPosition"
      :data-test="firstBlock.id"
      :hidden-devices="firstBlock.hiddenDevices"
      :props="firstBlock.props"
      :type="firstBlock.type"
    />
  </Container>

  <TheMainContent aria-labelledby="home-main-title">
    <div class="min-h-[100vh]">
      <!-- We display no placeholder component -->
      <!-- The content, style and order of these block can change at any moment -->
      <!-- Set a min height of viewport height, to avoid a flash of footer display -->

      <Container
        v-for="block in remainingBlocks"
        :key="block.id"
        :width="getBlockWidth(block.type)"
      >
        <BlockRenderer
          :id="block.id"
          :block-position="block.blockPosition"
          :data-test="block.id"
          :hidden-devices="block.hiddenDevices"
          :props="block.props"
          :type="block.type"
        />
      </Container>
    </div>
  </TheMainContent>
</template>

<script lang="ts" setup>
import { useHead, useRuntimeConfig } from '#imports'
import { computed } from 'vue'

import type { Banner } from '@backmarket/http-api/src/api-specs-content/models/banner'
import Container from '@backmarket/nuxt-layer-cms/Container.vue'
import SupportBanner from '@backmarket/nuxt-layer-cms/SupportBanner.vue'
import {
  getBlockWidth,
  isFullWidth,
} from '@backmarket/nuxt-layer-cms/utils/blockWidth'
import { useI18n } from '@backmarket/nuxt-module-i18n/useI18n'
import { useMarketplace } from '@backmarket/nuxt-module-marketplace/useMarketplace'
import { isEmpty } from '@backmarket/utils/object/isEmpty'

import TheMainContent from '~/components/TheMainContent/TheMainContent.vue'
import SkinnyBanner from '~/scopes/branding/components/SkinnyBanner/SkinnyBanner.vue'

import { useSkinnyBannerLoader } from '../branding/components/SkinnyBanner/useSkinnyBanner'
import { useCMSAlternateLinks } from '../cms/composables/useCMSAlternateLinks'
import { useCMSMeta } from '../cms/composables/useCMSMeta'
import { useFetchCMSPageContent } from '../cms/composables/useFetchCMSPageContent'
import { useFetchFallbackOnError } from '../cms/composables/useFetchFallbackOnError'
import { useFetchPageSupportBanner } from '../cms/composables/useFetchPageSupportBanner'
import { useHandleFetchError } from '../cms/composables/useHandleFetchError'
import { usePreloadFirstCarouselImage } from '../cms/composables/usePreloadFirstCarouselImage'
import { createSocialMediaTagsFor } from '../cms/helpers/meta/meta'
import BlockRenderer from '../cms/shared-components/BlockRenderer/BlockRenderer.vue'
import HeroView from '../cms/shared-components/HeroView/HeroView.vue'
import { useAddCanonicalLink } from '../seo/composables/useAddCanonicalLink'

import translations from './Home.translations'
import { useHomeSchema } from './utils/composables/useHomeSchema'
import { useHomeSocialMeta } from './utils/composables/useHomeSocialMeta'
import { getHtmlAttrsByCountry } from './utils/getHtmlAttrsByCountry'

const { load: loadSkinnyBannerData } = useSkinnyBannerLoader()
const fetchFallbackOnError = useFetchFallbackOnError()

const [{ data: homeData, error: homeError }] = await Promise.all([
  useFetchCMSPageContent({
    fetchFallbackOnError,
  }),
  loadSkinnyBannerData(),
])

usePreloadFirstCarouselImage(homeData)
useHandleFetchError(homeError)

const banner = computed<Banner | null>(() => {
  if (isEmpty(homeData.value?.banner)) return null

  return homeData.value?.banner as Banner
})

const blocksToRender = computed(() =>
  homeData.value?.blocks.map((block, index) => ({
    id: block.id,
    isFullWidth: isFullWidth(block.type),
    props: block.props,
    hiddenDevices: block.hiddenDevices,
    blockPosition: index + 1,
    type: block.type,
  })),
)

const firstBlock = computed(() => {
  return blocksToRender.value?.[0]
})

const firstBlockIsCarousel = computed(() => {
  return firstBlock.value?.type === 'Carousel'
})

const remainingBlocks = computed(() => {
  if (firstBlockIsCarousel.value) {
    return blocksToRender.value?.slice(1) || []
  }

  return blocksToRender.value || []
})

const {
  market: { countryCode },
} = useMarketplace()

const meta = useCMSMeta(homeData)
const i18n = useI18n()
const runtimeConfig = useRuntimeConfig()
const { COMPANY_NAME } = runtimeConfig.public
const alternateLinks = useCMSAlternateLinks(homeData)
const script = useHomeSchema(COMPANY_NAME)
const socials = useHomeSocialMeta()
const supportBanner = useFetchPageSupportBanner(homeData.value?.blockErrors)

useHead({
  title: homeData.value?.titleSeo,
  titleTemplate: i18n(translations.metaTitleTemplate),
  htmlAttrs: getHtmlAttrsByCountry(countryCode),
  link: alternateLinks,
  meta: [...meta, ...createSocialMediaTagsFor(socials)],
  script,
})

useAddCanonicalLink()
</script>
